import { AlertTriangle, Download, Wallet } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { useOrganizationWalletAllocation } from "../hooks/useOrganizationWalletAllocation";
import { useOrganizationWalletBalance } from "../hooks/useOrganizationWalletBalance";
import { usePocketsVisibility } from "../hooks/usePocketsVisibility";

const formatUSD = (raw: string | number) => {
  const value = typeof raw === "string" ? Number(raw) : raw;
  if (!Number.isFinite(value)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

/**
 * Container: wires the wallet balance / allocation hooks and navigation to the presentational view. Keep this
 * thin — all rendering lives in WalletBalanceBannerView so it can be tested in isolation (see
 * WalletBalanceBanner.stories.tsx).
 */
export const WalletBalanceBanner = () => {
  const navigate = useNavigate();

  const { balance, isLoading, error: rawError } = useOrganizationWalletBalance();
  const { aggregates, isLoading: isLoadingAllocation } = useOrganizationWalletAllocation();
  const { visible: pocketsVisible } = usePocketsVisibility();

  return (
    <WalletBalanceBannerView
      currentBalance={balance ? Number(balance.current.balance) : 0}
      pocketsTotal={aggregates?.pockets_total ?? 0}
      cardsLoadedTotal={aggregates?.cards_loaded_total ?? 0}
      showPockets={pocketsVisible}
      isLoading={isLoading || isLoadingAllocation}
      error={rawError ?? null}
      onMovements={() => navigate("/app/cards/movements")}
    />
  );
};

export interface WalletBalanceBannerViewProps {
  currentBalance: number;
  pocketsTotal: number;
  cardsLoadedTotal: number;
  /** Hide the pockets allocation card when the org is not using pockets. Defaults to shown. */
  showPockets?: boolean;
  isLoading: boolean;
  error: string | null;
  onMovements: () => void;
}

/** Presentational banner — pure function of its props (no hooks beyond i18n, no data fetching). */
export const WalletBalanceBannerView = ({
  currentBalance,
  pocketsTotal,
  cardsLoadedTotal,
  showPockets = true,
  isLoading,
  error,
  onMovements,
}: WalletBalanceBannerViewProps) => {
  const { t } = useTranslations("cards");

  const totalBalance = currentBalance + (showPockets ? pocketsTotal : 0) + cardsLoadedTotal;
  const allocationLoading = isLoading;

  const handleMovements = onMovements;

  return (
    <section
      className={cn(
        "relative overflow-hidden rounded-2xl shadow-sm",
        "bg-[linear-gradient(120deg,#003D31_0%,#00735C_55%,#00A380_100%)]",
        "dark:bg-[linear-gradient(120deg,#002820_0%,#00523F_55%,#007A5F_100%)]",
      )}
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-60"
        style={{
          backgroundImage: "radial-gradient(rgba(255,255,255,0.07) 1px, transparent 1px)",
          backgroundSize: "8px 8px",
        }}
      />

      <div className="relative p-6 lg:p-8 text-white">
        {error ? (
          <ColocaAlert
            variant="destructive"
            icon={AlertTriangle}
            heading={t("wallet_banner_error_load")}
            text={error}
            className="bg-white/10 text-white border-white/30 [&_[data-slot=alert-description]]:text-white/80"
          />
        ) : (
          <>
            <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2 text-white/70">
                  <Wallet className="h-4 w-4" aria-hidden="true" />
                  <span className="text-[11px] font-mono uppercase tracking-wider">
                    {t("wallet_banner_label")}
                  </span>
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/40 bg-emerald-400/15 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-emerald-100">
                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-300" />
                    {t("wallet_banner_status_active")}
                  </span>
                </div>

                <div className="mt-3 flex items-baseline gap-2">
                  {allocationLoading ? (
                    <ColocaSkeleton className="h-12 w-56 bg-white/20" />
                  ) : (
                    <>
                      <span className="text-4xl font-semibold font-mono tabular-nums tracking-tight text-white">
                        {formatUSD(totalBalance)}
                      </span>
                      <span className="text-base font-medium text-white/70">USD</span>
                    </>
                  )}
                </div>

                <p className="mt-2 max-w-xl text-xs text-white/70">{t("wallet_banner_description")}</p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <ColocaButton
                  variant="ghost"
                  icon={Download}
                  onClick={handleMovements}
                  className="text-white/90 hover:bg-white/15 hover:text-white dark:hover:bg-white/15"
                >
                  {t("wallet_banner_btn_movements")}
                </ColocaButton>
              </div>
            </div>

            <div
              className={cn("mt-6 grid grid-cols-1 gap-3", showPockets ? "md:grid-cols-3" : "md:grid-cols-2")}
            >
              <AllocationCard
                label={t("wallet_banner_allocation_available")}
                description={t("wallet_banner_allocation_available_description")}
                value={currentBalance}
                accent="rgba(255,255,255,0.95)"
                isLoading={allocationLoading}
              />
              {showPockets && (
                <AllocationCard
                  label={t("wallet_banner_allocation_pockets")}
                  description={t("wallet_banner_allocation_pockets_description")}
                  value={pocketsTotal}
                  accent="#7dd3a4"
                  isLoading={allocationLoading}
                />
              )}
              <AllocationCard
                label={t("wallet_banner_allocation_cards")}
                description={t("wallet_banner_allocation_cards_description")}
                value={cardsLoadedTotal}
                accent="#bef264"
                isLoading={allocationLoading}
              />
            </div>
          </>
        )}
      </div>
    </section>
  );
};

type AllocationCardProps = {
  label: string;
  description: string;
  value: number;
  accent: string;
  isLoading: boolean;
};

const AllocationCard = ({ label, description, value, accent, isLoading }: AllocationCardProps) => (
  <div className="rounded-md border border-white/15 bg-white/[0.08] p-3 backdrop-blur-sm">
    <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-white/65">
      <span className="inline-block h-2 w-2 rounded-sm" style={{ background: accent }} />
      <span>{label}</span>
    </div>
    {isLoading ? (
      <ColocaSkeleton className="mt-1 h-6 w-24 bg-white/20" />
    ) : (
      <p className="mt-1 text-xl font-semibold font-mono tabular-nums text-white">{formatUSD(value)}</p>
    )}
    <p className="mt-0.5 text-[11px] text-white/55">{description}</p>
  </div>
);
